import type { CSSProperties, ReactNode } from 'react';
import { X } from 'lucide-react';
import type { Child } from '../types';

export function ChildAvatar({
  child,
  size = 'medium',
  active = false
}: {
  child?: Child;
  size?: 'small' | 'medium' | 'large';
  active?: boolean;
}) {
  if (!child) {
    return <span className={`child-avatar child-avatar--${size}`} aria-hidden="true">?</span>;
  }
  return (
    <span
      className={`child-avatar child-avatar--${size} ${active ? 'is-active' : ''}`}
      style={{ '--avatar-color': child.avatarColor } as CSSProperties}
      title={child.name}
      aria-label={`${child.name}的头像`}
    >
      {child.avatarEmoji}
    </span>
  );
}

export function Modal({
  open,
  title,
  subtitle,
  children,
  onClose,
  size = 'medium',
  footer
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  size?: 'small' | 'medium' | 'large';
  footer?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭">
            <X size={22} />
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function ProgressRing({
  value,
  max,
  label,
  size = 108
}: {
  value: number;
  max: number;
  label: string;
  size?: number;
}) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <div
      className="progress-ring"
      style={
        {
          '--progress': `${Math.round(ratio * 360)}deg`,
          '--size': `${size}px`
        } as CSSProperties
      }
      aria-label={`${label} ${value}/${max}`}
    >
      <div className="progress-ring__inner">
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  ariaLabel
}: {
  items: Array<{ value: T; label: string; icon?: ReactNode; count?: number }>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {items.map((item) => (
        <button
          key={item.value}
          className={item.value === value ? 'is-active' : ''}
          type="button"
          role="tab"
          aria-selected={item.value === value}
          onClick={() => onChange(item.value)}
        >
          {item.icon}
          <span>{item.label}</span>
          {typeof item.count === 'number' ? <small>{item.count}</small> : null}
        </button>
      ))}
    </div>
  );
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="toast" role="status">
      {message}
    </div>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  action
}: {
  eyebrow?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-title">
      <div>
        {eyebrow ? <span>{eyebrow}</span> : null}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}
